import { images, type ImageName } from "../generated/images";
import { SITE } from "../lib/site";

export type ArticleCategory =
  | "Spatial Curation"
  | "Joinery & Millwork"
  | "Minimalist Theory"
  | "Material Studies"
  | "Lighting Design";

export type Article = {
  slug: string;
  title: string;
  /** Shorter title for small cards (home page). */
  shortTitle: string;
  category: ArticleCategory;
  /** Small footer tag on journal cards, e.g. "Bespoke Millwork". */
  topic: string;
  excerpt: string;
  /** ISO date, YYYY-MM-DD. */
  date: string;
  readMinutes: number;
  issue: number;
  image: ImageName;
  imageAlt: string;
  /** Image used for the homepage "Perspectives on Modern Living" cards. */
  homeImage?: ImageName;
  author: { name: string; role: string };
  lead?: boolean;
  featuredOnHome?: boolean;
  /** Per-article SEO: title topic (~25–35 chars), description (140–160 chars), 5–8 long-tail keywords. */
  seo?: { title: string; description: string; keywords: string[] };
  /** ISO date of the last meaningful edit (defaults to `date`). */
  updated?: string;
};

const AUTHOR = {
  name: "Kavitha Ramachandran",
  role: "Principal Spatial Stylist",
};

// NOTE: placeholder content from the Stitch design. Article bodies live in articleBodies.ts.
export const articles: Article[] = [
  {
    slug: "5-things-that-take-a-room-from-good-to-great",
    title:
      "5 Things That Take a Room from Good to Great: The Alchemy of Negative Space and Architectural Lighting",
    shortTitle: "5 Things That Take a Room from Good to Great",
    category: "Spatial Curation",
    topic: "Spatial Philosophy",
    excerpt:
      "True architectural luxury isn't an accumulation of ornamentation, but a disciplined subtraction. We dissect how the controlled interplay between unassigned void areas and grazing illumination transforms static rooms into breathing sanctuaries.",
    date: "2024-11-27",
    readMinutes: 6,
    issue: 45,
    image: "journal-lead-pavilion",
    imageAlt:
      "Monolithic living pavilion with recessed cove lighting, a fireplace and sculptural seating",
    homeImage: "journal-good-to-great",
    seo: {
      title: "Negative Space & Lighting Tips",
      description:
        "Five ways to take a room from good to great: negative space, layered lighting, filtered daylight and honest materials for homes in Nagercoil and Kerala.",
      keywords: [
        "negative space interior design tips",
        "layered lighting design for living rooms",
        "architectural lighting ideas for Indian homes",
        "how to make a room look luxurious",
        "interior design tips for Nagercoil homes",
        "warm white lighting for humid climate homes",
      ],
    },
    author: AUTHOR,
    lead: true,
    featuredOnHome: true,
  },
  {
    slug: "functional-and-stylish-wall-to-wall-shelves",
    title:
      "Functional and Stylish Wall-to-Wall Shelves: Crafting Tailored Architecture",
    shortTitle: "Functional and Stylish Wall-to-Wall Shelves",
    category: "Joinery & Millwork",
    topic: "Bespoke Millwork",
    excerpt:
      "How bespoke structural cabinetry maximizes spatial footprints while functioning as an expansive curatorial exhibition gallery in modern residences.",
    date: "2024-11-21",
    readMinutes: 6,
    issue: 44,
    image: "journal-oak-library",
    imageAlt:
      "Floor-to-ceiling smoked oak wall library with hidden warm strip lighting",
    homeImage: "journal-wall-shelves",
    seo: {
      title: "Wall-to-Wall Shelves Design Guide",
      description:
        "Plan bespoke wall-to-wall shelves that never sag or swell: spans, humidity-proof plywood, ventilation and built-in lighting for homes in coastal South India.",
      keywords: [
        "wall to wall shelves design ideas",
        "custom library wall units in Nagercoil",
        "humidity resistant plywood for shelves",
        "built-in bookshelf with LED lighting",
        "bespoke wall storage for living rooms",
        "carpentry for coastal homes in Kerala",
      ],
    },
    author: AUTHOR,
    featuredOnHome: true,
  },
  {
    slug: "the-5-secrets-to-pulling-off-simple-minimal-design",
    title:
      "The 5 Secrets to Pulling Off Simple, Minimal Design Without Sterility",
    shortTitle: "The 5 Secrets to Pulling Off Simple, Minimal Design",
    category: "Minimalist Theory",
    topic: "Tactile Warmth",
    excerpt:
      "Avoiding cold detachment through the layered introduction of tactile finishes, organic imperfections, and calibrated rhythmic light-wells.",
    date: "2024-11-15",
    readMinutes: 5,
    issue: 43,
    image: "journal-limewash-bedroom",
    imageAlt:
      "Sunlit minimalist bedroom with limewash plaster walls and a low timber platform bed",
    homeImage: "journal-minimal-design",
    seo: {
      title: "Warm Minimal Interior Design Tips",
      description:
        "Five secrets to minimal interiors that feel warm, not sterile: texture, warm palettes, hidden storage and calibrated daylight for South Indian homes.",
      keywords: [
        "warm minimalist interior design ideas",
        "minimal home design for Indian families",
        "lime plaster and Kota stone interiors",
        "hidden storage ideas for minimal homes",
        "minimalist interiors for humid climates",
        "minimal interior designers in Nagercoil",
      ],
    },
    author: AUTHOR,
    featuredOnHome: true,
  },
  {
    slug: "9-unique-and-unusual-ways-to-display-your-tv",
    title: "9 Unique and Architectural Ways to Display Your Art & Media Screen",
    shortTitle: "9 Architectural Ways to Display Your TV & Art",
    category: "Spatial Curation",
    topic: "Media Integration",
    excerpt:
      "Methods for dissolving unsightly black screens into holistic gallery walls using concealed sliding joinery, art frames, and magnetic fabric baffles.",
    date: "2024-11-08",
    readMinutes: 5,
    issue: 42,
    image: "journal-media-wall",
    imageAlt:
      "Living room gallery wall with slatted walnut panels concealing a media screen",
    seo: {
      title: "9 Stylish Ways to Display Your TV",
      description:
        "Nine architectural ways to display or hide your TV: sliding slats, gallery walls, niches and media walls, with glare, wiring and airflow tips for humid homes.",
      keywords: [
        "TV wall design ideas for living rooms",
        "hidden TV cabinet with sliding panels",
        "media wall design in Nagercoil",
        "how to hide a TV when not in use",
        "TV unit design for humid climates",
        "gallery wall around a television",
      ],
    },
    author: AUTHOR,
  },
  {
    slug: "biophilic-interiors-living-greenery-coastal-architecture",
    title:
      "Biophilic Interiors: Integrating Living Greenery into Coastal Architecture",
    shortTitle: "Biophilic Interiors for Coastal Homes",
    category: "Spatial Curation",
    topic: "Living Systems",
    excerpt:
      "Examining internal courtyards, subterranean soil drainage systems, and micro-climates that bridge exterior tropical landscapes with pristine interiors.",
    date: "2024-10-29",
    readMinutes: 5,
    issue: 41,
    image: "journal-biophilic-atrium",
    imageAlt:
      "Double-height coastal atrium with a sunken living room and an indoor ficus tree",
    seo: {
      title: "Biophilic Design for Coastal Homes",
      description:
        "Bring greenery into coastal homes the right way: courtyards, planter drainage, indoor plants for humid light and cross-ventilation for Kanyakumari and Kerala.",
      keywords: [
        "biophilic interior design for coastal homes",
        "courtyard home design in Kerala",
        "indoor plants for humid Indian homes",
        "indoor planter drainage and waterproofing",
        "nadumuttam style courtyard for modern homes",
        "green interior design in Kanyakumari",
      ],
    },
    author: AUTHOR,
  },
  {
    slug: "natural-stones-marble-travertine-quartzite-comparison",
    title:
      "The Longevity of Natural Stones: Marble, Travertine & Quartzite Comparison",
    shortTitle: "Marble vs Travertine vs Quartzite",
    category: "Material Studies",
    topic: "Geological Finishes",
    excerpt:
      "A definitive guide for homeowners detailing porosity, patina development, geological composition, and culinary durability in bespoke kitchen islands.",
    date: "2024-10-18",
    readMinutes: 6,
    issue: 40,
    image: "journal-natural-stones",
    imageAlt:
      "Side-by-side samples of honed Calacatta marble, travertine and quartzite",
    seo: {
      title: "Marble vs Travertine vs Quartzite",
      description:
        "Marble, travertine or quartzite? Compare etching, porosity, finishes and care, plus granite and Kota stone, to choose the right stone for your Indian kitchen.",
      keywords: [
        "marble vs quartzite for kitchen countertops",
        "travertine flooring pros and cons",
        "natural stone for Indian kitchens",
        "how to test quartzite before buying",
        "granite vs marble for Indian cooking",
        "Kota stone flooring for humid homes",
      ],
    },
    author: AUTHOR,
  },
  {
    slug: "acoustic-design-open-plan-homes",
    title:
      "Acoustic Mastery: Taming Reverberation in Modern Open-Plan Residences",
    shortTitle: "Acoustic Design for Open-Plan Homes",
    category: "Spatial Curation",
    topic: "Environmental Comfort",
    excerpt:
      "Invisible sound dampening strategies: acoustic plaster, micro-perforated wood ceiling slats, and tactile wall upholstery that maintain severe visual minimalism.",
    date: "2024-10-04",
    readMinutes: 5,
    issue: 39,
    image: "journal-acoustic-great-room",
    imageAlt:
      "Open-plan great room with micro-perforated oak ceiling panels and bouclé seating",
    seo: {
      title: "Acoustic Design for Open-Plan Homes",
      description:
        "Reduce echo in open-plan homes without spoiling the look: acoustic ceilings, slatted timber, fabric panels, rugs and zoning, using humidity-tolerant materials.",
      keywords: [
        "acoustic design for open plan homes",
        "how to reduce echo in living rooms",
        "wooden slat acoustic ceiling panels",
        "soundproofing ideas for Indian homes",
        "acoustic panels for humid climates",
        "reduce rain noise on metal roofs",
      ],
    },
    author: AUTHOR,
  },
];

/** Absolute URL of the article's largest JPEG (for JSON-LD and social cards). */
export function articleImageUrl(a: Article) {
  const img = images[a.image];
  return `${SITE.origin}/images/${a.image}-${img.widths[img.widths.length - 1]}.jpg`;
}

export const getArticle = (slug: string) =>
  articles.find((a) => a.slug === slug);

/** "27 Nov 2024" — formatted deterministically (no locale/timezone dependence). */
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
export function formatDate(iso: string, withYear = true) {
  const [y, m, d] = iso.split("-").map(Number);
  return `${String(d).padStart(2, "0")} ${MONTHS[m - 1]}${withYear ? ` ${y}` : ""}`;
}
